import { filterVisibleColumns, resolveChartFunctionId } from './dashboardPortability';
import { parseLayoutItems, isTextLayoutItem, isChartLayoutItem } from './dashboardLayoutItems';
import { enrichVizSpec, buildChartExecuteFilter, buildChartFilterSignature, formatReportingDisplayValue, normalizeFilterId } from './reportingChartUtils';
import { buildStatViewModel, isStatVizType } from './reportingStatCard';
import {
    processChartDataset,
    pickDatasetFromResult,
    chartExecuteCacheKey,
    runWithConcurrencyPool,
} from './reportingChartCache';
import { listCohortsByIds } from '../../../actions/cohortActions';
import { listUploadBatchesByIds } from '../../../actions/uploadBatchActions';
import { executeReportFunction } from '../../../actions/reportingActions';

export const PRINT_CAPTURE_WIDTH = 720;
export const PRINT_CAPTURE_HEIGHT = 400;
export const PRINT_TABLE_MAX_ROWS = 100;

function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function formatCellValue(value, columnType) {
    if (typeof value === 'number' && Number.isFinite(value)) return value.toLocaleString();
    return formatReportingDisplayValue(value, columnType);
}

export function buildDatasetTableHtml(dataset, { maxRows = PRINT_TABLE_MAX_ROWS } = {}) {
    if (!dataset?.rows?.length) {
        return '<p class="section-empty">No data available.</p>';
    }

    const columns = filterVisibleColumns(dataset.columns ?? []);
    if (columns.length === 0) {
        return '<p class="section-empty">No columns to display.</p>';
    }

    const rows = dataset.rows.slice(0, maxRows);
    const truncated = dataset.rows.length > maxRows;

    const thead = columns
        .map(col => `<th>${escapeHtml(col.label || col.key)}</th>`)
        .join('');

    const tbody = rows.map((row) => {
        const cells = columns
            .map(col => `<td>${escapeHtml(formatCellValue(row[col.key], col.type))}</td>`)
            .join('');
        return `<tr>${cells}</tr>`;
    }).join('');

    const footnote = truncated
        ? `<p class="table-note">Showing first ${maxRows} of ${dataset.rows.length.toLocaleString()} rows.</p>`
        : '';

    return `
        <table class="data-table">
            <thead><tr>${thead}</tr></thead>
            <tbody>${tbody}</tbody>
        </table>
        ${footnote}
    `;
}

export function buildStatCardHtml(dataset, vizSpec = {}) {
    const model = buildStatViewModel(dataset, vizSpec);
    if (!model) return '<p class="print-empty">No data available.</p>';

    const { display, items, total, valueLabel } = model;

    if (display === 'total' || items.length <= 1) {
        const primary = items.length === 1 ? items[0] : null;
        const title = primary?.label && primary.label !== valueLabel ? primary.label : valueLabel;
        const value = primary && items.length === 1 ? primary.value : total;
        return `
            <div class="stat-card">
                <div class="stat-label">${escapeHtml(title)}</div>
                <div class="stat-value">${escapeHtml(formatCellValue(value))}</div>
            </div>
        `;
    }

    const tiles = items.slice(0, 12).map(item => `
        <div class="stat-tile">
            <div class="stat-label">${escapeHtml(item.label || '—')}</div>
            <div class="stat-value">${escapeHtml(formatCellValue(item.value))}</div>
        </div>
    `).join('');

    const more = items.length > 12
        ? `<p class="table-note">+${items.length - 12} more categories (total ${formatCellValue(total)})</p>`
        : '';

    return `<div class="stat-grid">${tiles}</div>${more}`;
}

export function buildTextBlockHtml(item) {
    const title = item.title?.trim();
    const description = item.description?.trim();
    if (!title && !description) return '';

    const titleHtml = title ? `<h3 class="section-header">${escapeHtml(title)}</h3>` : '';
    const bodyHtml = description
        ? `<p class="text-body">${escapeHtml(description).replace(/\n/g, '<br/>')}</p>`
        : '';

    return `<div class="text-block section">${titleHtml}<div class="section-content">${bodyHtml}</div></div>`;
}

function formatDateRangeBound(value) {
    if (!value) return null;
    if (value.format) {
        const hasTime = value.hour() !== 0 || value.minute() !== 0 || value.second() !== 0;
        return value.format(hasTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD');
    }
    const s = String(value);
    return s.length > 10 && /T|\s\d{2}:\d{2}/.test(s) ? s.slice(0, 16).replace('T', ' ') : s.slice(0, 10);
}

export function buildDateRangeLabel(dateRange) {
    if (dateRange?.[0] && dateRange?.[1]) {
        return `${formatDateRangeBound(dateRange[0])} to ${formatDateRangeBound(dateRange[1])}`;
    }
    if (dateRange?.[0]) {
        return `From ${formatDateRangeBound(dateRange[0])}`;
    }
    if (dateRange?.[1]) {
        return `Until ${formatDateRangeBound(dateRange[1])}`;
    }
    return 'All';
}

export function buildSessionLabel(cohortId, resolvedName) {
    if (!cohortId) return 'All';
    return resolvedName?.trim() || String(cohortId);
}

export function buildUploadBatchLabel(uploadBatchId, resolvedName) {
    if (!uploadBatchId) return 'All';
    return resolvedName?.trim() || String(uploadBatchId);
}

export function buildFilterSummaryLines(dateRange, globalFilters = {}, filterLabels = {}) {
    const lines = [];
    lines.push(`Session: ${buildSessionLabel(globalFilters.cohortId, filterLabels.session)}`);
    lines.push(`Upload batch: ${buildUploadBatchLabel(globalFilters.uploadBatchId, filterLabels.uploadBatch)}`);
    lines.push(`Date range: ${buildDateRangeLabel(dateRange)}`);
    return lines;
}

export async function resolveReportingFilterLabels(globalFilters = {}, dispatch, getState) {
    const cohortId = normalizeFilterId(globalFilters.cohortId);
    const uploadBatchId = normalizeFilterId(globalFilters.uploadBatchId);
    const labels = {};

    if (cohortId) {
        await dispatch(listCohortsByIds([cohortId]));
        const cohorts = getState()?.cohortListByIds?.cohorts ?? [];
        const cohort = cohorts.find(c => String(c.id ?? c.Id) === String(cohortId));
        labels.session = cohort?.cohortName ?? cohort?.name ?? null;
    }

    if (uploadBatchId) {
        await dispatch(listUploadBatchesByIds([uploadBatchId]));
        const batches = getState()?.uploadBatchListByIds?.uploadBatches ?? [];
        const batch = batches.find(b => String(b.id ?? b.Id) === String(uploadBatchId));
        labels.uploadBatch = batch?.fileName ?? batch?.FileName ?? null;
    }

    return labels;
}

export function shouldCaptureChartImage(vizSpec = {}) {
    if (isStatVizType(vizSpec)) return false;
    if (vizSpec.mode === 'table') return false;
    if (vizSpec.type === 'table') return false;
    return true;
}

export function shouldIncludeTable(vizSpec = {}) {
    if (isStatVizType(vizSpec)) return false;
    return true;
}

async function loadDashboardDatasets(dashboard, dateRange, globalFilters, dispatch, labelMapsLookup = {}, functions = []) {
    const layoutItems = parseLayoutItems(dashboard?.layoutJson);
    const chartItems = layoutItems.filter(isChartLayoutItem);
    if (chartItems.length === 0) return { layoutItems, datasets: {}, errors: {} };

    const filter = buildChartExecuteFilter({}, dateRange, globalFilters);
    const filterKey = buildChartFilterSignature(dateRange, globalFilters);
    const datasets = {};
    const errors = {};

    const uniqueByFunction = new Map();
    for (const chart of chartItems) {
        const functionId = resolveChartFunctionId(chart, functions, dashboard?.name);
        if (!functionId) continue;
        const key = chartExecuteCacheKey(functionId, filterKey);
        if (!uniqueByFunction.has(key)) {
            uniqueByFunction.set(key, { functionId, charts: [] });
        }
        uniqueByFunction.get(key).charts.push(chart);
    }

    const executeGroup = async (functionId, chartsForFn) => {
        const result = await dispatch(executeReportFunction(functionId, filter));
        if (result?.error) {
            for (const chart of chartsForFn) {
                errors[chart.id] = result.error;
                datasets[chart.id] = null;
            }
            return;
        }

        for (const chart of chartsForFn) {
            const rawDataset = pickDatasetFromResult(result, chart.datasetKey);
            if (rawDataset) {
                datasets[chart.id] = processChartDataset(chart, rawDataset, labelMapsLookup);
            } else {
                errors[chart.id] = 'Error loading chart';
                datasets[chart.id] = null;
            }
        }
    };

    const tasks = [...uniqueByFunction.values()].map(
        ({ functionId, charts: chartsForFn }) => () => executeGroup(functionId, chartsForFn),
    );
    await runWithConcurrencyPool(tasks);

    return { layoutItems, datasets, errors };
}

export async function loadSelectedDashboardsPrintData({
    dashboards,
    dateRange,
    globalFilters,
    dispatch,
    labelMapsLookup = {},
    functions = [],
}) {
    const results = [];

    for (const dashboard of dashboards) {
        const { layoutItems, datasets, errors } = await loadDashboardDatasets(
            dashboard,
            dateRange,
            globalFilters,
            dispatch,
            labelMapsLookup,
            functions,
        );

        const captureItems = [];
        for (const item of layoutItems) {
            if (isTextLayoutItem(item)) continue;
            const vizSpec = enrichVizSpec(item.viz, item.dimensionFilter);
            const dataset = datasets[item.id] ?? null;
            if (!dataset && !errors[item.id]) continue;
            if (shouldCaptureChartImage(vizSpec) && dataset?.rows?.length) {
                captureItems.push({
                    id: item.id,
                    dataset,
                    vizSpec,
                    title: item.title,
                });
            }
        }

        results.push({
            dashboard,
            layoutItems,
            datasets,
            errors,
            captureItems,
        });
    }

    return results;
}

export function buildSectionHtml({
    item,
    dataset,
    error,
    chartImage,
    vizSpec,
}) {
    const title = item.title?.trim() || 'Chart';
    const description = item.description?.trim();
    const parts = [`<div class="chart-section section no-break">`];
    parts.push(`<h3 class="section-header">${escapeHtml(title)}</h3>`);
    if (description) {
        parts.push(`<p class="section-desc">${escapeHtml(description)}</p>`);
    }
    parts.push('<div class="section-content">');

    if (error) {
        parts.push(`<p class="section-error">${escapeHtml(error)}</p>`);
    } else if (!dataset?.rows?.length) {
        parts.push('<p class="section-empty">No data available.</p>');
    } else if (isStatVizType(vizSpec)) {
        parts.push(buildStatCardHtml(dataset, vizSpec));
    } else {
        if (shouldCaptureChartImage(vizSpec)) {
            if (chartImage) {
                parts.push(`<div class="chart-panel"><img src="${chartImage}" alt="${escapeHtml(title)}" /></div>`);
            } else {
                parts.push('<p class="section-empty">Chart could not be rendered for PDF.</p>');
            }
        }
        if (shouldIncludeTable(vizSpec)) {
            parts.push(buildDatasetTableHtml(dataset));
        }
    }

    parts.push('</div></div>');
    return parts.join('');
}

export function buildDashboardHtml({
    dashboardName,
    layoutItems,
    datasets,
    errors,
    chartImages,
}) {
    const sections = layoutItems.map((item) => {
        if (isTextLayoutItem(item)) {
            const html = buildTextBlockHtml(item);
            return html ? `<div class="no-break">${html}</div>` : '';
        }

        const vizSpec = enrichVizSpec(item.viz, item.dimensionFilter);
        return buildSectionHtml({
            item,
            dataset: datasets[item.id],
            error: errors[item.id],
            chartImage: chartImages[item.id],
            vizSpec,
        });
    }).filter(Boolean);

    return `
        <div class="dashboard-block">
            <h2 class="page-heading">${escapeHtml(dashboardName)}</h2>
            ${sections.join('')}
        </div>
    `;
}

export function buildReportingPrintPayload({
    dashboardResults,
    chartImagesByDashboard,
    userInfo,
    dateRange,
    globalFilters,
    filterLabels = {},
}) {
    const now = new Date();
    const generatedDate = now.toLocaleDateString();
    const generatedDateTime = now.toLocaleString();
    const sessionLabel = buildSessionLabel(globalFilters?.cohortId, filterLabels.session);
    const uploadBatchLabel = buildUploadBatchLabel(globalFilters?.uploadBatchId, filterLabels.uploadBatch);
    const dateRangeLabel = buildDateRangeLabel(dateRange);
    const filterLines = buildFilterSummaryLines(dateRange, globalFilters, filterLabels);

    const dashboardHtmlParts = dashboardResults.map((result) => {
        const dashId = result.dashboard.id ?? result.dashboard.Id;
        return buildDashboardHtml({
            dashboardName: result.dashboard.name ?? 'Dashboard',
            layoutItems: result.layoutItems,
            datasets: result.datasets,
            errors: result.errors,
            chartImages: chartImagesByDashboard[dashId] ?? {},
        });
    });

    const dashboardNames = dashboardResults
        .map(r => r.dashboard.name)
        .filter(Boolean)
        .join(', ');

    return {
        reportHeader: {
            content: {
                organizationName: userInfo?.organizationName || 'OpenEP Health Data Platform',
                generatedDate,
                reportId: `RPT-SQL-${Date.now()}`,
                sessionLabel,
                uploadBatchLabel,
                dateRangeLabel,
                filterSummary: filterLines.join(' · '),
                dashboardCount: dashboardResults.length,
            },
        },
        reportBody: {
            content: {
                html: dashboardHtmlParts.join('<div class="page-break"></div>'),
            },
        },
        reportFooter: {
            content: {
                generatedDateTime,
                generatedBy: userInfo?.email || 'System',
                generatedByName: userInfo?.name || userInfo?.email || 'System',
                dashboardNames,
            },
        },
    };
}
